import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  compose,
  setDisplayName,
} from 'recompose';
import { get, noop, sortBy, orderBy, compact } from 'lodash';
import qs from 'qs';
import { withTracker } from 'components/Tracker';
import WarningIcon from '@material-ui/icons/WarningRounded';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { grey700 } from '@material-ui/core/colors';

import immutableToJS from 'helpers/immutableToJS';
import { dispatchRestMaker, toCurrency, includesSearchText } from 'helpers/utils';
import {
  injectIntl,
  ConfirmAction,
  SidebarItems,
  errorFallback,
} from 'components';
import { clearAuthToken, loginOrganization, refreshToken } from 'redux/auth/actions';
import { accessTokenSelector } from 'redux/auth/selectors';
import { getNotifications } from 'redux/notifications/actions';
import memberActions from 'redux/members/actions';
import buyersActions from 'redux/buyers/actions';
import sellersActions from 'redux/sellers/actions';
import { buyersAcceptActions } from 'redux/buyers/actions';
import { sellersAcceptActions } from 'redux/sellers/actions';
import { selectState } from 'redux/selectors';
import {
  setLocale,
  setScrollTop,
} from 'redux/ui/actions';
import {
  clearCartLocalStorage,
} from 'helpers/utils';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Header from './Header';
import Sidebar from './Sidebar';

const styles = () => ({
  acceptBtn: {
    margin: 5,
    background: '#4caf50',
    color: '#fff',
  },
});

class App extends Component {
  static contextTypes = {
    currentMember: PropTypes.shape({}),
    currentUser: PropTypes.shape({}),
    isDistributor: PropTypes.bool,
    isFarmer: PropTypes.bool,
    isRestaurant: PropTypes.bool,
    isConsumer: PropTypes.bool,
    isMember: PropTypes.bool,
  };

  static propTypes = {
    accessToken: PropTypes.string,
    children: PropTypes.node.isRequired,
    classes: PropTypes.shape({}).isRequired,
    history: PropTypes.shape({}),
    permissions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
    })),
    clearAuthToken: PropTypes.func.isRequired,
    loginOrganization: PropTypes.func.isRequired,
    formatMessage: PropTypes.func.isRequired,
    refreshToken: PropTypes.func.isRequired,
    setLocale: PropTypes.func.isRequired,
    setScrollTop: PropTypes.func.isRequired,
    getAppNotifications: PropTypes.func.isRequired,
    getStoreNotifications: PropTypes.func.isRequired,
    removeMember: PropTypes.func.isRequired,
    updateMember: PropTypes.func.isRequired,
    buyers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ).isRequired,
    listBuyers: PropTypes.func.isRequired,
    sellers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ).isRequired,
    listSellers: PropTypes.func.isRequired,
    createBuyersAccept: PropTypes.func.isRequired,
    createSellersAccept: PropTypes.func.isRequired,
    removeBuyer: PropTypes.func.isRequired,
    removeSeller: PropTypes.func.isRequired,
  };

  static defaultProps = {
    accessToken: null,
    history: {},
    permissions: [],
  };

  static childContextTypes = {
    readOnly: PropTypes.bool,
    refreshToken: PropTypes.func,
  };

  constructor(props, context) {
    super(props);
    const { history } = props;
    const valueString = history.location.search.slice(1);
    const { organizationEmail, ...rest } = qs.parse(valueString);
    this.state = { noRedirect: false, fillOutDialogOpen: false };
    if (organizationEmail) {
      const organization = context.currentUser.organizations.find(
        org => org.organizationEmail === organizationEmail);
      if (organization &&
        (!context.currentMember || context.currentMember.id !== organization.id)
      ) {
        this.state.noRedirect = true;
        props.loginOrganization({
          memberId: organization.id,
        });
      } else {
        history.push(`${history.location.pathname}?${qs.stringify(rest)}`);
      }
    }
  }

  getChildContext() {
    const { currentMember } = this.context;
    const { history, refreshToken } = this.props;
    let context = {};
    if (currentMember && currentMember.permissions) {
      const currentPermission = currentMember.permissions.find(permission =>
        history.location.pathname.startsWith(`/${permission.frontendUrl}`));

      if (currentPermission && currentPermission.frontendUrl === 'settings' && currentMember.role === 'admin') {
        currentPermission.memberPermissions.writeAccess = true;
      }

      context = {
        readOnly: currentPermission ? !currentPermission.memberPermissions.writeAccess : false,
        refreshToken,
      };
    } else {
      context = {
        readOnly: false,
        refreshToken,
      };
    }
    return context;
  }

  componentWillMount() {
    const { getAppNotifications, getStoreNotifications } = this.props;
    this.redirectMember(this.props);
    this.setLocale();
    getAppNotifications();
    getStoreNotifications();
  }

  componentDidMount() {
    this.onReload();
  }

  onReload = () => {
    const { listBuyers, listSellers } = this.props;
    const isRetailPage = window.location.href.indexOf('retail') !== -1;
    listBuyers({ isRetail: isRetailPage });
    listSellers();
  }

  setLocale = () => {
    const { currentUser } = this.context;
    const { setLocale } = this.props;
    const locale = get(currentUser, 'account.profile.locale') || process.env.LANGUAGE || 'en';
    setLocale(locale);
  }

  getFilteredContacts = (contacts, isVendor) => {
    const filteredContacts = contacts.filter(contact =>
      contact.requestorId &&
      (isVendor ?
        contact.requestorId === contact.sellerId && !contact.isDeleted :
        contact.requestorId === contact.buyerId && !contact.isDeleted
      ),
    );
    return filteredContacts;
  }

  getBuyers = () => {
    const { buyers } = this.props;
    const contactProfileKey = 'buyerProfile';
    const contacts = buyers;
    const { searchText } = this.state;
    const sortedContacts = orderBy(
      contacts,
    );
    const filteredContacts = sortedContacts.filter(contact => includesSearchText(searchText,
      contact[contactProfileKey] ? compact([
        contactProfileKey === 'buyerProfile' ? contact.buyerCompanyCode : contact.sellerCompanyCode,
        contactProfileKey === 'buyerProfile' ? contact.buyerGroupName : contact.sellerGroupName,
        contact[contactProfileKey].companyName,
        contact[contactProfileKey].email,
        contact[contactProfileKey].firstName,
        contact[contactProfileKey].lastName,
      ]) : [],
    ));
    return filteredContacts;
  };

  getSellers = () => {
    const { sellers } = this.props;
    const contactProfileKey = 'sellerProfile';
    const contacts = sellers;
    const { searchText } = this.state;
    const sortedContacts = orderBy(
      contacts,
    );
    const filteredContacts = sortedContacts.filter(contact => includesSearchText(searchText,
      contact[contactProfileKey] ? compact([
        contactProfileKey === 'buyerProfile' ? contact.buyerCompanyCode : contact.sellerCompanyCode,
        contactProfileKey === 'buyerProfile' ? contact.buyerGroupName : contact.sellerGroupName,
        contact[contactProfileKey].companyName,
        contact[contactProfileKey].email,
        contact[contactProfileKey].firstName,
        contact[contactProfileKey].lastName,
      ]) : [],
    ));
    return filteredContacts;
  };

  isDeactivated = () => {
    const { currentMember, currentUser } = this.context;
    let deactivated = false;
    if (!currentMember && currentUser) {
      const {
        account: {
          profile: {
            firstName,
            lastName,
            mobile,
            companyName,
            addresses,
          },
        },
        roleId,
      } = currentUser || { account: { profile: { addresses: [{}] } } };

      const { address, city, state, zipcode } = addresses && addresses.length ? addresses[0] : {};

      if (roleId && (!firstName || !lastName || !mobile || !companyName
        || !address || !city || !state || !zipcode)) {
        deactivated = true;
      }
      if (!roleId && (!firstName || !lastName || !mobile)) {
        deactivated = true;
      }
    }
    return deactivated;
  }

  handleFillOutDialogClose = () => {
    this.setState({ fillOutDialogOpen: false });
  }

  acceptInvite = (contact, isVendor) => {
    const {
      history,
      createBuyersAccept,
      createSellersAccept,
    } = this.props;
    if (isVendor) {
      createSellersAccept(contact.sellerId, {
        onSuccess: () => {
          if (this.isDeactivated()) {
            this.setState({ fillOutDialogOpen: true });
            this.onReload();
            history.push('/settings');
          } else {
            this.onReload();
          }
        },
      });
    } else {
      createBuyersAccept(contact.buyerId, {
        onSuccess: () => {
          if (this.isDeactivated()) {
            this.setState({ fillOutDialogOpen: true });
            this.onReload();
            history.push('/settings');
          } else {
            this.onReload();
          }
        },
      });
    }
  };

  handleAcceptInviteContact = (connect, isVendor) => {
    this.acceptInvite(connect, isVendor);
  };

  handleRemoveContactInfo = (contact, isVendor) => {
    const { removeBuyer, removeSeller } = this.props;
    if (isVendor) {
      removeSeller(contact.id, {
        onSuccess: () => {
          this.onReload();
        },
      });
    } else {
      removeBuyer(contact.id, {
        onSuccess: () => {
          this.onReload();
        },
      });
    }
  };

  handleFillOutDialogClose = () => {
    this.setState({ fillOutDialogOpen: false });
  }

  redirectMember = ({ history, permissions }) => {
    const { currentMember } = this.context;
    const { noRedirect } = this.state;
    if (currentMember && currentMember.permissions) {
      const originalPermission = permissions.find(permission => !permission.isDivider
        && history.location.pathname.startsWith(`/${permission.frontendUrl}`));
      if (originalPermission) {
        const currentPermission = currentMember.permissions.find(permission =>
          history.location.pathname.startsWith(`/${permission.frontendUrl}`));
        const trackOrdersPermission = currentMember.permissions.find(permission => permission.frontendUrl === 'track-orders');
        const firstUrl = trackOrdersPermission ? trackOrdersPermission.frontendUrl :
          (currentMember.permissions.find(permission => !permission.isDivider) || {}).frontendUrl;
        const path = firstUrl || `/no-permission#page=${originalPermission.name}`;
        if (!currentPermission && !noRedirect) {
          this.props.history.push({
            pathname: `/${path}`,
            search: `?org=${currentMember.memberAccountId}`,
          });
        }
      }
    }
  }

  handleLogout = () => {
    memoryDB.tokenInfo = JSON.stringify({ data: {} });
    clearCartLocalStorage();
    window.location = '/';
  };

  handleAcceptInvite = (memberId, organizationId) => {
    const { history, refreshToken, updateMember } = this.props;
    updateMember(memberId, {
      organizationId,
      pending: false,
      onSuccess: () => {
        if (this.isDeactivated()) {
          this.setState({ fillOutDialogOpen: true });
          refreshToken();
          history.push('/settings');
        } else {
          refreshToken();
        }
      },
    });
  };

  handleRemoveInvite = (memberId) => {
    this.props.removeMember(memberId, { onSuccess: this.props.refreshToken });
  };

  renderInvitesAsVendor = () => {
    const filteredContacts = this.getBuyers();
    const isVendor = false;
    const contacts = this.getFilteredContacts(filteredContacts, isVendor);
    if (contacts.length === 0) {
      return null;
    }
    const { classes, formatMessage } = this.props;
    const pendingInvitations = sortBy(contacts, ({ buyerProfile }) => buyerProfile.companyName);
    if (!pendingInvitations) {
      return null;
    }
    return pendingInvitations.map(contact => (
      <div
        className="banner invite-banner"
        key={contact.id}
      >
        {formatMessage('Vendor Invitation: {companyName} wants to be your Customer', {
          companyName: contact.buyerProfile.companyName,
        })}
        <ConfirmAction
          action={() => this.handleAcceptInviteContact(contact, isVendor)}
          description={formatMessage('Do you want {companyName} as your Customer?',  {
            companyName: contact.buyerProfile.companyName,
          })}
          style={{ margin: '0 20px 0 50px' }}
        >
          <Button
            classes={{ root: classes.acceptBtn }}
            variant="contained"
          >
            {formatMessage('Accept')}
          </Button>
        </ConfirmAction>
        <ConfirmAction
          action={() => this.handleRemoveContactInfo(contact)}
          description={formatMessage('Do you really want to discard this Vendor Invitation?')}
        >
          <DeleteIcon color={grey700} />
        </ConfirmAction>
      </div>
    ));
  };

  renderInvitesAsCustomer = () => {
    const filteredContacts = this.getSellers();
    const isVendor = true;
    const contacts = this.getFilteredContacts(filteredContacts, isVendor);
    if (contacts.length === 0) {
      return null;
    }
    const { classes, formatMessage } = this.props;
    const pendingInvitations = sortBy(contacts, ({ sellerProfile }) => sellerProfile.companyName);
    if (!pendingInvitations) {
      return null;
    }
    return pendingInvitations.map(contact => (
      <div
        className="banner invite-banner"
        key={contact.id}
      >
        {formatMessage('Customer Invitation: {companyName} wants to be your Vendor', {
          companyName: contact.sellerProfile.companyName,
        })}
        <ConfirmAction
          action={() => this.handleAcceptInviteContact(contact, isVendor)}
          description={formatMessage('Do you want {companyName} as your Vendor?',  {
            companyName: contact.sellerProfile.companyName,
          })}
          style={{ margin: '0 20px 0 50px' }}
        >
          <Button
            classes={{ root: classes.acceptBtn }}
            variant="contained"
          >
            {formatMessage('Accept')}
          </Button>
        </ConfirmAction>
        <ConfirmAction
          action={() => this.handleRemoveContactInfo(contact)}
          description={formatMessage('Do you really want to discard this Customer Invitation?')}
        >
          <DeleteIcon color={grey700} />
        </ConfirmAction>
      </div>
    ));
  };

  renderMemberInvites = () => {
    const { currentUser: { organizations } } = this.context;
    const { classes, formatMessage } = this.props;

    const pendingInvitations = sortBy(organizations.filter(
      item => item.pending,
    ), ({ account }) => account.profile.companyName);

    if (!pendingInvitations) {
      return null;
    }

    return pendingInvitations.map(({
      id,
      account,
      accountId,
    }) => (
      <div
        className="banner invite-banner"
        key={id}
      >
        {formatMessage('You have a Member invitation from {companyName}', {
          companyName: account.profile.companyName,
        })}
        <ConfirmAction
          action={() => this.handleAcceptInvite(id, accountId)}
          description={formatMessage('Do you really want to join this organization?')}
          style={{ margin: '0 20px 0 50px' }}
        >
          <Button
            classes={{ root: classes.acceptBtn }}
            variant="contained"
          >
            {formatMessage('Accept')}
          </Button>
        </ConfirmAction>
        <ConfirmAction
          action={() => this.handleRemoveInvite(id)}
          description={formatMessage('Do you really want to discard this invitation?')}
        >
          <DeleteIcon color={grey700} />
        </ConfirmAction>
      </div>
    ));
  };

  render() {
    const {
      currentMember,
      currentUser,
      isDistributor,
      isFarmer,
      isConsumer,
      isMember,
    } = this.context;
    const {
      accessToken,
      children,
      clearAuthToken,
      permissions,
      formatMessage,
      setScrollTop,
      history,
      ...props
    } = this.props;
    const { noRedirect, fillOutDialogOpen } = this.state;
    let deactivated = false;
    if (!currentMember && currentUser) {
      const {
        account: {
          profile: {
            firstName,
            lastName,
            mobile,
            companyName,
            addresses,
          },
        },
      } = currentUser || { account: { profile: { addresses: [{}] } } };
      const { address, city, state, zipcode } = addresses && addresses.length ? addresses[0] : {};
      if (noRedirect) {
        return (
          <div className="main-progress">
            <CircularProgress />
          </div>
        );
      }
      if (!firstName || !lastName || !mobile || !companyName
        || !address || !city || !state || !zipcode) {
        if (get(currentUser, 'account.currentRoleId')) {
          if (history.location.pathname !== '/settings') {
            return <Redirect to="/settings#deactivated" />;
          }
          deactivated = true;
        } else if (history.location.pathname !== '/settings') {
          return <Redirect to="/settings" />;
        }
      }
    }
    // set nav-bar items for each mode.
    let filterOption = 'inBuyer';
    if (isFarmer) {
      filterOption = 'inSeller';
    } else if (isDistributor) {
      filterOption = 'inDistributor';
    } else if (isConsumer) {
      filterOption = 'inConsumer';
    }
    let items = permissions.filter(permission => permission[filterOption]);
    if (currentMember && currentMember.permissions) {
      items = items.filter(permission =>
        permission.isDivider || currentMember.permissions.find(({ id }) => permission.id === id),
      );
      items = items.filter((permission, index) =>
        !(permission.isDivider && (index === 0 || items[index - 1].isDivider)),
      );
    }
    const balance = get(currentUser, 'account.creditUsable');
    const adminMessage = get(currentUser, 'account.adminMessage');
    if (!currentUser.role) {
      items = [];
    }
    items = items.map(permission => (
      permission.isDivider
        ? { divider: true }
        : {
          name: formatMessage(permission.name),
          url: `/${permission.frontendUrl}`,
        }
    ));

    items.push({ space: true });
    items.push({ divider: true });
    items.push(
      {
        handleClick: this.handleLogout,
        icon: 'fa fa-sign-out',
        name: formatMessage('Logout'),
      },
    );

    return (
      <div className="app vh_height100">
        <Header
          formatMessage={formatMessage}
          history={history}
          {...props}
          {...currentUser}
        />
        <SidebarItems
          items={items}
        />
        <div className="app-body">
          <main
            className="main"
            id="main"
            onScroll={({ target }) => setScrollTop(target.scrollTop)}
          >
            {adminMessage && (
              <div className="banner credit-warning">
                {adminMessage}
              </div>
            )}
            {balance < 0 && (
              <div className="banner credit-warning" onClick={isMember ? noop : () => history.push('/payments')} style={{ display: 'none' }}>
                <WarningIcon />
                {' '}{isMember ? formatMessage("Organization's current balance is") : formatMessage('Your current balance is')}
                {' '}{toCurrency(balance)}
              </div>
            )}
            {!currentMember && this.renderMemberInvites()}
            {!currentMember && this.renderInvitesAsVendor()}
            {!currentMember && this.renderInvitesAsCustomer()}
            {children}
          </main>
        </div>
        <Sidebar
          currentMember={currentMember}
          currentUser={currentUser}
          history={history}
          deactivated={deactivated}
          {...props}
        />
        <Dialog
          open={fillOutDialogOpen}
          onClose={this.handleFillOutDialogClose}
          >
          <DialogTitle></DialogTitle>
          <DialogContent>
            <DialogContentText style={{ color: '#151b1e' }}>
              Success. Now complete required fields below.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleFillOutDialogClose} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  accessToken: accessTokenSelector(state),
  ...selectState('options', 'permissions')(state, 'permissions'),
  ...selectState('buyers', 'buyers')(state, 'buyers'),
  ...selectState('sellers', 'sellers')(state, 'sellers'),
});

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
  ...dispatchRestMaker([
    [memberActions, 'member'],
    [buyersActions, 'buyer'],
    [sellersActions, 'seller'],
    [buyersAcceptActions, 'buyersAccept'],
    [sellersAcceptActions, 'sellersAccept'],
  ])(dispatch),
  clearAuthToken: () => dispatch(clearAuthToken()),
  loginOrganization: payload => dispatch(loginOrganization(payload)),
  refreshToken: () => dispatch(refreshToken()),
  getAppNotifications: () => dispatch(getNotifications('app')),
  getStoreNotifications: () => dispatch(getNotifications('store')),
  setLocale: payload => dispatch(setLocale(payload)),
  setScrollTop: scrollTop => dispatch(setScrollTop(scrollTop)),
});

export default compose(
  setDisplayName('App'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  immutableToJS,
  injectIntl,
  errorFallback,
  withTracker,
  withStyles(styles),
)(App);
